import { useEffect, useMemo, useState } from "react";
import { CardContent } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import CardWithAvatar from "components/CardWithAvatar";
import Weather from "components/Weather";
import EventApi, { EventRow, WeatherInterval } from "api/event";
import useNotifier from "hooks/useNotifier";
import weatherAnimations from "assets/weather/weather-animations";
import { weatherIconsLabels } from "assets/weather/weather-icons";

dayjs.extend(utc);

/**
 * Generate placeholders data for days of which the weather data
 * is not available
 *
 * @param eventEndDate
 * @param data
 * @returns
 */
function generatePlaceholders(eventEndDate: string, data: WeatherInterval[][]): dayjs.Dayjs[] {
  const placeholderDates: dayjs.Dayjs[] = [];
  const lastDay = dayjs
    .unix(data[data.length - 1][0].dt)
    .utcOffset(0)
    .startOf("day");

  for (let i = 1; i <= dayjs(eventEndDate).diff(lastDay, "day"); i += 1) {
    placeholderDates.push(lastDay.add(i, "day"));
  }

  return placeholderDates;
}

export interface EventWeatherProps {
  event: EventRow;
}

function EventWeather(props: EventWeatherProps): JSX.Element {
  const { event } = props;
  const [interval, setInterval] = useState<WeatherInterval | undefined>();
  const [placeholders, setPlaceholders] = useState<dayjs.Dayjs[]>([]);
  const { showNotification } = useNotifier();
  const WeatherIcon = interval?.main ? weatherAnimations[interval.main] : weatherAnimations.Clouds;
  const eventApi = useMemo(() => new EventApi(showNotification), [showNotification]);

  /**
   * Set initial interval and generate placeholders
   */
  useEffect(() => {
    if (event.weather?.data && event.weather.data?.length > 0) {
      setInterval(event.weather.data[0][0]);
      setPlaceholders(generatePlaceholders(event.end_date, event.weather.data));
    } else {
      setInterval(undefined);
    }
  }, [event.weather, event.end_date]);

  /**
   * Update event weather if 1 hour passed since last update and only if event
   * did not started
   * TODO: -when implementing security take in consideration if event is open.
   *       - keep old weather if event is closed and fetch only for the following days
   */
  useEffect(() => {
    (async () => {
      if (
        event.status === "open" &&
        dayjs(event.start_date).diff(dayjs(), "day") <= 4 &&
        dayjs().isBefore(event.end_date) &&
        event.weather &&
        event.location
      ) {
        if (!event.weather.updated_at) {
          await eventApi.updateWeather(event.id);
        } else if (dayjs().diff(event.weather.updated_at, "hour") >= 1) {
          await eventApi.updateWeather(event.id);
        }
      }
    })();
  }, [event.start_date, event.end_date, event.weather?.data, event.location]);

  return (
    <CardWithAvatar
      icon={
        <>
          <title id='weather-icon'>
            {interval?.main ? weatherIconsLabels[interval.main] : weatherIconsLabels.Clouds}
          </title>
          <WeatherIcon aria-labelledby='weather-icon' />
        </>
      }
      noHeaderGutterBottom
      title='Weather'
      subheader={interval ? dayjs.unix(interval.dt).utcOffset(0).format("MMMM DD, HH:mm") : undefined}
    >
      <CardContent>
        <Weather
          eventLength={dayjs(event.end_date).diff(event.start_date, "day")}
          data={event.weather?.data}
          placeholders={placeholders}
          onChangeWeather={(newInterval) => setInterval(newInterval)}
        />
      </CardContent>
    </CardWithAvatar>
  );
}

export default EventWeather;
